import { useCallback, useEffect, useRef } from "react";

import type {
  MessengerOutgoingEvent,
  MessengerScope,
  UuidString,
} from "../types/messengerEvents";

/** typing.<scope>.start / stop の payload 最大値（共通）。 */
interface TypingIds {
  project_id: UuidString;
  shot_id?: UuidString;
  dm_id?: UuidString;
  parent_id?: UuidString | null;
  /** `scope = "task_board"` のセル特定用（タスク行 ID）。 */
  task_id?: string;
  /** `scope = "task_board"` のセル特定用（列 ID。タイトル列は `"__title"`）。 */
  column_id?: string;
}

interface UseTypingSenderParams {
  /** MessengerSocketProvider 経由の送信関数。 */
  send: (ev: MessengerOutgoingEvent) => boolean;
  /** 対象スコープ。 */
  scope: MessengerScope;
  /**
   * 送信 payload に含める ID 集合。
   * `scope = "project"` は project_id のみ、`"shot_comment"` は project_id + shot_id、
   * `"dm"` は project_id + dm_id、`"task_board"` は project_id + task_id +
   * column_id が必須。parent_id は任意。
   */
  ids: TypingIds;
  /**
   * 有効化フラグ。例えば送信先が未確定（dm_id 未選択など）のときは false にして
   * send を実行しないようにする。デフォルト true。
   */
  enabled?: boolean;
}

const START_DEBOUNCE_MS = 500;
const IDLE_STOP_MS = 5000;

/**
 * 入力中（Typing）送信フック。
 *
 * BE 仕様書「Messenger リアルタイム連携」§4.1 / 「WebSocket 仕様書」§3.3 準拠。
 *
 * - `onTypingChange` 呼び出しを 500ms debounce し `typing.<scope>.start` を送信。
 * - 最終入力から 5s 経過、または `onTypingCommit`（blur / 送信）で `typing.<scope>.stop` を送信。
 * - 直近に送った状態（start/stop）を保持し、同じ状態の重複送信はスキップ（サーバ負荷軽減）。
 * - unmount 時に必ず stop を送信（取りこぼし防止）。
 */
export const useTypingSender = ({
  send,
  scope,
  ids,
  enabled = true,
}: UseTypingSenderParams): {
  onTypingChange: () => void;
  onTypingCommit: () => void;
} => {
  const lastSentRef = useRef<"start" | "stop" | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 毎レンダで最新の props を参照するため ref を介する（依存配列の変動を避ける）。
  const sendRef = useRef(send);
  sendRef.current = send;
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const clearTimers = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const buildPayload = useCallback(() => {
    const { project_id, shot_id, dm_id, parent_id, task_id, column_id } =
      idsRef.current;
    switch (scopeRef.current) {
      case "project":
        return { project_id, parent_id: parent_id ?? null };
      case "shot_comment":
        if (!shot_id) return null;
        return { project_id, shot_id, parent_id: parent_id ?? null };
      case "dm":
        if (!dm_id) return null;
        return { project_id, dm_id, parent_id: parent_id ?? null };
      case "task_board":
        if (!task_id || !column_id) return null;
        return { project_id, task_id, column_id };
      default:
        return null;
    }
  }, []);

  const sendStart = useCallback(() => {
    if (!enabledRef.current) return;
    if (lastSentRef.current === "start") return;
    const payload = buildPayload();
    if (!payload) return;
    const ok = sendRef.current({
      type: `typing.${scopeRef.current}.start` as MessengerOutgoingEvent["type"],
      payload,
    } as MessengerOutgoingEvent);
    if (ok) {
      lastSentRef.current = "start";
    }
  }, [buildPayload]);

  const sendStop = useCallback(() => {
    // 一度も start を送っていなければ stop も送らない（初期状態）。
    if (lastSentRef.current === null || lastSentRef.current === "stop") return;
    const payload = buildPayload();
    if (!payload) {
      // payload が作れなくても lastSent は「送信済み」なのでリセットしておく。
      lastSentRef.current = "stop";
      return;
    }
    const ok = sendRef.current({
      type: `typing.${scopeRef.current}.stop` as MessengerOutgoingEvent["type"],
      payload,
    } as MessengerOutgoingEvent);
    if (ok) {
      lastSentRef.current = "stop";
    }
  }, [buildPayload]);

  const onTypingChange = useCallback(() => {
    if (!enabledRef.current) return;

    // start は 500ms debounce。連続入力中は再送しない（lastSent === "start" でスキップ）。
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      sendStart();
    }, START_DEBOUNCE_MS);

    // idle タイマーは毎回延長（最終入力から 5s 無反応なら stop）。
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      sendStop();
    }, IDLE_STOP_MS);
  }, [sendStart, sendStop]);

  const onTypingCommit = useCallback(() => {
    clearTimers();
    sendStop();
  }, [clearTimers, sendStop]);

  // scope / primary id が変わったら内部状態をリセットする。
  // parent_id は scopeKey に含めない（スレッド内の返信切替で毎回リセットすると
  // debounce タイマーが頻繁に消えてしまうため）。
  // 旧スコープ向けの stop は取りこぼしても、受信側の 7s auto-clear で解消される。
  const scopeKey = `${scope}:${ids.project_id}:${ids.shot_id ?? ""}:${ids.dm_id ?? ""}:${ids.task_id ?? ""}:${ids.column_id ?? ""}`;
  useEffect(() => {
    clearTimers();
    lastSentRef.current = null;
  }, [scopeKey, clearTimers]);

  // unmount で stop を保証（取りこぼし防止）。
  useEffect(
    () => () => {
      clearTimers();
      sendStop();
    },
    [clearTimers, sendStop],
  );

  return { onTypingChange, onTypingCommit };
};
